import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@repo/ui/src/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@repo/ui/src/components/ui/sidebar';
import { useAuthStore } from '../../stores/authStore';
import { OrgAvatar } from './OrgAvatar';
import { RolePill } from './RolePill';
import { OrgMenuItems } from './OrgMenuPanel';
import type { OtherOrg } from './orgMenuItems';

/** Sidebar-footer organisation menu (desktop/tablet). In the icon rail the
 *  trigger collapses to the logo alone, with the org name as its tooltip —
 *  `size="lg"` carries `group-data-[collapsible=icon]:!p-0` for exactly that. */
export function OrgMenu({ otherOrgs = [] }: { otherOrgs?: OtherOrg[] }) {
  const { t } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const organization = useAuthStore((s) => s.organization);
  const role = useAuthStore((s) => s.role);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg" aria-label={t('orgMenu.trigger')} tooltip={organization?.name || 'LopHoc.app'}>
          <OrgAvatar
            name={organization?.name}
            logoUrl={organization?.logoUrl}
            className="size-8 group-data-[collapsible=icon]:size-full"
          />
          <div className="grid flex-1 text-left leading-tight group-data-[collapsible=icon]:hidden">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-[13px] font-semibold text-surface-800">{organization?.name || 'LopHoc.app'}</span>
              {role && <RolePill role={role} />}
            </span>
            <span className="truncate text-[11px] text-surface-400">{user?.fullName || ''}</span>
          </div>
          <ChevronsUpDown className="ml-auto size-4 text-surface-400 group-data-[collapsible=icon]:hidden" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      {/* w-[18rem], not w-72: this content is portalled to <body>, outside the
          sidebar's --spacing: 0.25rem scope, where w-72 would be 230px. */}
      <DropdownMenuContent side="right" align="end" className="w-[18rem]">
        <OrgMenuItems variant="menu" otherOrgs={otherOrgs} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
